'use client';

import { Icon, type IconName } from '../../../components/icons';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/Button';
import { cn } from '../../../lib/utils';

export type ShareChannelHandlers = {
  onShareEmail: () => void;
  onShareFacebook: () => void;
  onShareInstagram: () => void;
  onShareTiktok: () => void;
};

/** Figma mobile share shell: dark panel, pink icon tiles, white label. */
export type ShareOverlayPalette = 'default' | 'figmaDark';

export type ShareToSocialsPanelProps = ShareChannelHandlers & {
  /** `row` = icon strip (mobile Top 3 footer); `column` = labeled list (desktop sidebar / list slide). */
  layout?: 'row' | 'column';
  /** When true, Email is disabled (e.g. no persisted `matchResultsId`). */
  emailDisabled?: boolean;
  /** When true, social platform shares are disabled (e.g. no persisted results URL). */
  socialDisabled?: boolean;
  /** Dark overlay styling used inside `MatchShareResultsFlow`. */
  palette?: ShareOverlayPalette;
  className?: string;
};

type SocialPlatform = {
  id: IconName;
  label: string;
  onShare: () => void;
};

export function ShareToSocialsPanel({
  layout = 'column',
  onShareEmail,
  onShareFacebook,
  onShareInstagram,
  onShareTiktok,
  emailDisabled = false,
  socialDisabled = false,
  palette = 'default',
  className,
}: ShareToSocialsPanelProps) {
  const { t } = useTranslation('matching');
  const dark = palette === 'figmaDark';

  const itemClassDefault =
    layout === 'row'
      ? 'flex flex-col items-center gap-2 rounded-xl border border-border bg-card p-3 text-center shadow-sm'
      : 'flex w-full items-center justify-start gap-3 rounded-xl border border-border bg-card px-3 py-3 text-left shadow-sm transition-colors hover:bg-accent/60';

  /** Figma mobile: full pink touch tiles, no nested card chrome. */
  const itemClassDarkRow =
    'flex size-12 min-h-12 min-w-12 items-center justify-center rounded-xl border-0 bg-primary-600 p-0 text-primary-foreground shadow-none hover:bg-primary-500 sm:size-14 sm:min-h-14 sm:min-w-14';

  const itemClassDarkCol =
    'flex w-full items-center justify-start gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-left text-white shadow-none transition-colors hover:bg-white/10';

  const itemClass =
    dark && layout === 'row'
      ? itemClassDarkRow
      : dark && layout === 'column'
        ? itemClassDarkCol
        : itemClassDefault;

  const iconWrapDefault =
    'flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary-600 text-primary-foreground';

  const socialPlatforms: SocialPlatform[] = [
    { id: 'facebook', label: 'Facebook', onShare: onShareFacebook },
    { id: 'instagram', label: 'Instagram', onShare: onShareInstagram },
    { id: 'tiktok', label: 'TikTok', onShare: onShareTiktok },
  ];

  const shareDisabledTitle = t('share.disabled');

  return (
    <div
      className={cn(
        'rounded-2xl border p-4 shadow-sm',
        dark
          ? 'border-white/10 bg-[#352a47]/90 backdrop-blur-sm'
          : 'border-border bg-card/80',
        layout === 'row' && 'flex flex-col gap-3',
        layout === 'column' && 'flex flex-col gap-2',
        className,
      )}
    >
      <p
        className={cn(
          'mb-1 w-full text-sm font-semibold',
          dark ? 'text-white/95' : 'text-foreground',
        )}
      >
        {t('share.socials')}
      </p>
      <div
        className={cn(
          layout === 'row' &&
            'flex w-full flex-wrap justify-start gap-2 sm:gap-3',
          layout === 'column' && 'flex flex-col items-start gap-2',
        )}
      >
        <Button
          type='button'
          variant={
            dark && layout === 'row'
              ? 'primary'
              : dark && layout === 'column'
                ? 'ghost'
                : 'secondary'
          }
          className={cn(
            itemClass,
            !(dark && layout === 'row') && 'h-auto min-h-0 shadow-sm',
          )}
          aria-label={t('share.via', { channel: t('email.email') })}
          aria-disabled={emailDisabled || undefined}
          disabled={emailDisabled}
          title={emailDisabled ? t('share.emailDisabled') : undefined}
          onClick={onShareEmail}
        >
          {dark && layout === 'row' ? (
            <Icon name='mail-filled' className='size-6 sm:size-7' />
          ) : (
            <>
              <span className={cn(iconWrapDefault)}>
                <Icon name='mail-filled' className='size-5' />
              </span>
              {layout === 'column' ? (
                <span
                  className={cn(
                    'font-medium',
                    dark ? 'text-white' : 'text-foreground',
                  )}
                >
                  Email
                </span>
              ) : null}
            </>
          )}
        </Button>
        {socialPlatforms.map((platform) => (
          <Button
            key={platform.id}
            type='button'
            variant={
              dark && layout === 'row'
                ? 'primary'
                : dark && layout === 'column'
                  ? 'ghost'
                  : 'secondary'
            }
            className={cn(
              itemClass,
              !(dark && layout === 'row') && 'h-auto min-h-0 shadow-sm',
            )}
            aria-label={t('share.via', { channel: platform.label })}
            aria-disabled={socialDisabled || undefined}
            disabled={socialDisabled}
            title={socialDisabled ? shareDisabledTitle : undefined}
            onClick={platform.onShare}
          >
            {dark && layout === 'row' ? (
              <Icon name={platform.id} className='size-6 sm:size-7' />
            ) : (
              <>
                <span className={cn(iconWrapDefault)}>
                  <Icon name={platform.id} className='size-5' />
                </span>
                {layout === 'column' ? (
                  <span
                    className={cn(
                      'font-medium',
                      dark ? 'text-white' : 'text-foreground',
                    )}
                  >
                    {platform.label}
                  </span>
                ) : null}
              </>
            )}
          </Button>
        ))}
      </div>
    </div>
  );
}
